import { useId } from "react";
import { useQuery } from "@tanstack/react-query";
import { ClipboardList, Info, RotateCw } from "lucide-react";
import { api } from "../api";
import "./preparation-coverage.css";

export interface PreparationPreview {
  taxYear: number;
  status: "REVIEW_REQUIRED" | "NEEDS_INFORMATION";
  topics: {
    id: string;
    title: string;
    status: "review" | "needs_information";
    description: string;
  }[];
  notices: {
    id: string;
    severity: "blocking" | "warning" | "info";
    message: string;
  }[];
  capabilities: { calculation: false; prepareForms: false; efile: false };
}

interface Props {
  caseId: string;
  revision: number;
  hasUnsavedChanges?: boolean;
}

export function PreparationCoverage({
  caseId,
  revision,
  hasUnsavedChanges = false,
}: Props) {
  const headingId = useId();
  const query = useQuery({
    queryKey: [
      "preparation-preview",
      caseId,
      revision,
      hasUnsavedChanges ? "unsaved" : "saved",
    ],
    queryFn: ({ signal }) =>
      api<PreparationPreview>(
        `/cases/${encodeURIComponent(caseId)}/preparation-preview`,
        { method: "GET", signal },
      ),
    enabled: Boolean(caseId) && !hasUnsavedChanges,
    placeholderData: undefined,
    retry: false,
    refetchOnWindowFocus: false,
  });
  // A disabled query can retain cached data. Never present that data as current
  // while the user has edits that have not reached the saved return.
  const preview =
    caseId && !hasUnsavedChanges && !query.isError ? query.data : undefined;

  return (
    <section className="preparation-coverage" aria-labelledby={headingId}>
      <header className="pc-header">
        <div className="pc-heading">
          <span className="pc-icon" aria-hidden="true">
            <ClipboardList size={21} />
          </span>
          <div>
            <h3 id={headingId}>Preparation coverage</h3>
            <p>Topics identified from the saved return.</p>
          </div>
        </div>
        {preview && <span className="pc-year">Tax year {preview.taxYear}</span>}
      </header>

      <div className="pc-body" aria-live="polite" aria-atomic="false">
        {!caseId ? (
          <p className="pc-state">
            Select a return to review its preparation topics.
          </p>
        ) : hasUnsavedChanges ? (
          <div className="pc-state" role="status">
            <strong>Save your changes to update coverage</strong>
            <p>
              This review uses saved information. Your latest changes will be
              included after you save.
            </p>
          </div>
        ) : query.isPending ? (
          <div className="pc-state" role="status">
            <strong>Reviewing saved information…</strong>
            <p>Gathering the topics and questions for this return.</p>
          </div>
        ) : query.isError ? (
          <div className="pc-state pc-error" role="alert">
            <strong>Preparation coverage could not be loaded</strong>
            <p>Your saved information is unchanged. Please try again.</p>
            <button
              type="button"
              className="pc-retry"
              onClick={() => void query.refetch()}
              disabled={query.isFetching}
            >
              <RotateCw size={15} aria-hidden="true" />
              {query.isFetching ? "Trying again…" : "Try again"}
            </button>
          </div>
        ) : preview ? (
          <>
            <div className="pc-summary">
              <span
                className={`pc-status ${preview.status === "NEEDS_INFORMATION" ? "pc-status-needed" : ""}`}
              >
                <span aria-hidden="true" />
                {preview.status === "NEEDS_INFORMATION"
                  ? "More information needed"
                  : "Review required"}
              </span>
              <p>
                Review each topic and confirm eligibility using the taxpayer’s
                records.
              </p>
            </div>
            {preview.topics.length ? (
              <ul className="pc-topics" aria-label="Preparation topics">
                {preview.topics.map((topic) => (
                  <li key={topic.id}>
                    <div className="pc-topic-heading">
                      <h4>{topic.title}</h4>
                      <span
                        className={`pc-topic-status ${topic.status === "needs_information" ? "pc-topic-needed" : ""}`}
                      >
                        {topic.status === "needs_information"
                          ? "Needs information"
                          : "Needs review"}
                      </span>
                    </div>
                    <p>{topic.description}</p>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="pc-state pc-empty">
                <strong>No preparation topics identified yet</strong>
                <p>
                  Add and save the taxpayer’s information to build this review.
                  An empty list does not mean the return is complete.
                </p>
              </div>
            )}
            {preview.notices.length > 0 && (
              <div className="pc-notices">
                <h4>Items to follow up</h4>
                <ul>
                  {preview.notices.map((notice) => (
                    <li key={notice.id}>
                      <Info size={16} aria-hidden="true" />
                      <span>{notice.message}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        ) : (
          <p className="pc-state">
            Coverage is unavailable. Please try again after saving the return.
          </p>
        )}
      </div>

      <footer className="pc-footer">
        <Info size={16} aria-hidden="true" />
        <p>
          Based on saved information. This is not a completed tax calculation.
          <span>
            Tax calculation, prepared forms, and electronic filing are not
            available here.
          </span>
        </p>
      </footer>
    </section>
  );
}
